// 2026-09-24 - FormuLearning - Step 6 입력 경로·연동 비교·투과 등급 헬퍼 (이슈 #63)
// 파일 위치: frontend/src/features/step6/pkModel.ts
// 연동값(`linked`)은 필드 경로 → {value, source}. 입력값과 저장 당시 연동 스냅샷(inputs.linked)을 비교해
// «수정됨»을 칠하고, 서버가 준 stale 경로로 «상위 값 변경»을 표시한다(개정안 §3.3 BR-S6-08).

import type { PkInputs } from '../../api/step6'

export function getPath(obj: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((cur, k) => (cur && typeof cur === 'object' ? (cur as Record<string, unknown>)[k] : undefined), obj)
}

export function setPath<T>(obj: T, path: string, value: unknown): T {
  const clone = structuredClone(obj) as Record<string, unknown>
  const keys = path.split('.')
  let cur = clone
  for (const k of keys.slice(0, -1)) {
    if (!cur[k] || typeof cur[k] !== 'object') cur[k] = {}
    cur = cur[k] as Record<string, unknown>
  }
  cur[keys[keys.length - 1]] = value
  return clone as T
}

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null)

/** 입력값이 저장 당시 연동값과 다른가 — 연동값이 없던 칸(null)은 «수정» 이 아니라 «입력» 이다. */
export function isEdited(inputs: PkInputs, path: string): boolean {
  const snap = inputs.linked?.[path]
  if (!snap || snap.value == null) return false
  return !same(getPath(inputs, path), snap.value)
}

/** 군 안에서 연동값과 달라진 경로들. */
export function editedPaths(inputs: PkInputs, paths: string[]): string[] {
  return paths.filter((p) => isEdited(inputs, p))
}

/** 경로들을 저장 당시 연동값으로 되돌린다. */
export function revert(inputs: PkInputs, paths: string[]): PkInputs {
  return paths.reduce((acc, p) => setPath(acc, p, structuredClone(inputs.linked?.[p]?.value ?? null)), inputs)
}

/** 경로들을 현재 연동값으로 다시 불러오고 스냅샷도 갱신한다(stale 해소). */
export function reload(inputs: PkInputs, paths: string[], linked: Record<string, { value: unknown; source: string }>): PkInputs {
  // linked 의 키는 점을 품은 경로 문자열이라 setPath 로 쓰지 않고 사전을 직접 갱신한다.
  let next = inputs
  const snap = { ...(inputs.linked ?? {}) }
  for (const p of paths) {
    const item = linked[p]
    if (!item) continue
    next = setPath(next, p, structuredClone(item.value))
    snap[p] = { value: structuredClone(item.value), source: item.source }
  }
  return { ...next, linked: snap }
}

/** 투과 등급 — 엔진 `classify`(pk_engine.py · 아티팩트 JS)와 같은 표. 화면 즉시 표시용(S6-SIM-02). */
export function classifyPapp(method: 'Caco-2' | 'PAMPA', papp: number): [string, number] | null {
  if (!Number.isFinite(papp) || papp < 0) return null
  const edges = method === 'Caco-2' ? [1e-6, 5e-6, 10e-6, 20e-6] : [0.1e-6, 1e-6, 5e-6, 10e-6]
  if (papp < edges[0]) return ['Very low', 0.3e-4]
  if (papp < edges[1]) return ['Low', 0.5e-4]
  if (papp < edges[2]) return ['Moderate', 1.0e-4]
  if (papp <= edges[3]) return ['High', 2.5e-4]
  return ['Very high', 3.5e-4]
}

/** 고급 설정 표(개정안 §3.4). def = 서버 기본값(prefill.ADVANCED_DEFAULTS)과 같게 유지. */
export const ADVANCED_FIELDS: { group: string; key: string; label: string; unit: string; def: number }[] = [
  { group: '위장관 생리', key: 'k_ge_solid_h', label: 'Gastric emptying — solid', unit: 'h⁻¹', def: 1 },
  { group: '위장관 생리', key: 'k_ge_dissolved_h', label: 'Gastric emptying — dissolved', unit: 'h⁻¹', def: 2.8 },
  { group: '위장관 생리', key: 'gastric_fluid_ml', label: 'Gastric fluid volume', unit: 'mL', def: 250 },
  { group: '위장관 생리', key: 'upper_fluid_ml', label: 'Upper intestine fluid volume', unit: 'mL', def: 342 },
  { group: '위장관 생리', key: 'lower_fluid_ml', label: 'Lower intestine fluid volume', unit: 'mL', def: 231 },
  { group: '투과', key: 'distal_region_factor', label: 'Distal permeability factor', unit: '—', def: 0.5 },
  { group: '투과', key: 'area_volume_ratio_cm_inv', label: 'Area / volume ratio', unit: 'cm⁻¹', def: 1.14 },
  { group: '석출/재용해', key: 'k_prec_upper_h', label: 'Precipitation rate — upper', unit: 'h⁻¹', def: 1 },
  { group: '석출/재용해', key: 'k_prec_lower_h', label: 'Precipitation rate — lower', unit: 'h⁻¹', def: 1 },
  { group: '석출/재용해', key: 'k_rediss_base_h', label: 'Redissolution rate', unit: 'h⁻¹', def: 1 },
  { group: '계산설정', key: 'dt_h', label: 'Integration step (dt)', unit: 'h', def: 0.01 },
  { group: '계산설정', key: 'body_weight_kg', label: 'Body weight (Vd L/kg 환산)', unit: 'kg', def: 70 },
]

/** 용출액 pH 의 부위 이름(아티팩트 표기). */
export function mediumName(ph: number): string {
  if (Math.abs(ph - 1.2) < 1e-9) return 'Gastric'
  if (Math.abs(ph - 4.0) < 1e-9 || Math.abs(ph - 4.5) < 1e-9) return 'Upper intestine'
  if (ph >= 6.5) return 'Lower intestine'
  return ''
}
